const css = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;700;900&family=Inter:wght@400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    /* ── Silverback Surface Hierarchy ── */
    --bg:          #131313;
    --bg-lowest:   #0e0e0e;
    --bg-low:      #1c1b1b;
    --bg-mid:      #201f1f;
    --bg-high:     #2a2a2a;
    --bg-highest:  #353534;
    --topbar-bg:   #0c0e12;

    /* ── Legacy aliases (keep views working) ── */
    --bg2:     #1c1b1b;
    --bg3:     #0e0e0e;
    --surface: #2a2a2a;
    --surface2:#353534;

    /* ── Silverback Palette ── */
    --primary:     #b9c8de;
    --primary-dim: #94a3b8;
    --tertiary:    #e1c19d;
    --error:       #ffb4ab;

    /* ── Legacy accent aliases (used in views) ── */
    --accent:   #b9c8de;
    --accent-d: #94a3b8;
    --accent-g: rgba(185,200,222,0.10);
    --teal:     #e1c19d;
    --teal-g:   rgba(225,193,157,0.12);
    --green:    #b9c8de;
    --green-g:  rgba(185,200,222,0.10);
    --orange:   #e1c19d;
    --red:      #ffb4ab;
    --red-g:    rgba(255,180,171,0.12);
    --req:      #ffb4ab;
    --slate:    #c4c6cd;

    /* ── Text ── */
    --text:     #e5e2e1;
    --muted:    #8e9197;
    --dim:      #44474c;
    --on-primary: #233143;

    /* ── Borders ── */
    --border:  rgba(68,71,76,0.18);
    --border2: rgba(68,71,76,0.35);
  }

  /* Light theme */
  [data-theme="light"] {
    --bg:          #f2f3f5;
    --bg-lowest:   #e6e8ec;
    --bg-low:      #ffffff;
    --bg-mid:      #eceef1;
    --bg-high:     #dde0e6;
    --bg-highest:  #d0d4db;
    --topbar-bg:   #1a1d23;
    --bg2:     #ffffff;
    --bg3:     #e6e8ec;
    --surface: #dde0e6;
    --surface2:#d0d4db;
    --text:    #0f1117;
    --muted:   #4a5568;
    --dim:     #718096;
    --on-primary: #ffffff;
    --border:  rgba(0,0,0,0.12);
    --border2: rgba(0,0,0,0.22);
    --accent:  #3b5a8a;
    --accent-d:#2d4a75;
    --accent-g:rgba(59,90,138,0.1);
    --primary: #3b5a8a;
    --primary-dim: #5272a0;
    --tertiary:    #7a5c3a;
    --teal:    #7a5c3a;
    --teal-g:  rgba(122,92,58,0.1);
    --green:   #3b5a8a;
    --green-g: rgba(59,90,138,0.1);
    --orange:  #7a5c3a;
    --red:     #b91c1c;
    --red-g:   rgba(185,28,28,0.08);
    --req:     #b91c1c;
    --slate:   #4a5568;
    --error:   #b91c1c;
  }

  html { height: 100%; }
  body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    background: var(--bg);
    color: var(--text);
    min-height: 100vh;
    font-size: 13px;
    line-height: 1.5;
    padding-left: env(safe-area-inset-left);
    padding-right: env(safe-area-inset-right);
  }

  /* ── HEADLINE FONT ──────────────────────────────────────────────────── */
  .headline, h1.headline, h2.headline, h3.headline {
    font-family: 'Space Grotesk', sans-serif;
    letter-spacing: -0.02em;
  }
  .label-caps {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--muted);
  }

  /* ── BRUSHED-METAL TEXTURE ───────────────────────────────────────────── */
  .brushed-metal {
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
    opacity: 0.025;
    pointer-events: none;
    position: absolute;
    inset: 0;
  }

  /* ── APP SHELL ──────────────────────────────────────────────────────── */
  .app { display: flex; flex-direction: column; min-height: 100vh; }
  .main { flex: 1; display: flex; flex-direction: column; min-height: 100vh; }

  /* ── TOPBAR ─────────────────────────────────────────────────────────── */
  .topbar {
    position: fixed; top: 0; left: 0; right: 0; z-index: 50;
    height: 64px;
    background: var(--topbar-bg);
    display: flex; align-items: center; justify-content: space-between;
    padding: 0 20px;
    border-bottom: 1px solid var(--border);
  }
  .topbar-logo {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 22px; font-weight: 900;
    letter-spacing: -0.04em;
    text-transform: uppercase;
    color: #94a3b8;
  }
  .topbar-avatar {
    width: 32px; height: 32px; border-radius: 8px;
    background: var(--bg-highest);
    display: flex; align-items: center; justify-content: center;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 12px; font-weight: 700; color: var(--primary);
    cursor: pointer; border: 1px solid var(--border2);
    transition: opacity 0.15s;
    flex-shrink: 0;
  }
  .topbar-avatar:hover { opacity: 0.85; }
  .topbar-left { display: flex; align-items: center; gap: 12px; }
  .topbar-right { display: flex; align-items: center; gap: 8px; }
  .topbar-action {
    background: none; border: none; cursor: pointer;
    color: var(--muted); padding: 6px; border-radius: 8px;
    transition: all 0.15s; display: flex; align-items: center;
  }
  .topbar-action:hover { background: var(--bg-low); color: var(--text); }

  /* ── CONTENT AREA ───────────────────────────────────────────────────── */
  .content {
    padding: 20px 16px;
    flex: 1;
    max-width: 480px;
    margin: 0 auto;
    width: 100%;
    padding-top: calc(64px + 20px);
    padding-bottom: calc(72px + 60px);
  }

  /* ── BOTTOM NAV ─────────────────────────────────────────────────────── */
  .bottom-nav {
    position: fixed; bottom: 0; left: 0; right: 0; z-index: 50;
    background: var(--bg-low);
    border-top: 1px solid rgba(68,71,76,0.35);
    border-radius: 12px 12px 0 0;
    display: flex; justify-content: space-around; align-items: center;
    padding: 8px 8px max(8px, env(safe-area-inset-bottom));
    height: auto; min-height: 60px;
  }
  .nav-item {
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    gap: 3px; padding: 8px 12px; border-radius: 8px; cursor: pointer;
    transition: all 0.15s; color: #64748b;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 9px; font-weight: 700; letter-spacing: 0.12em;
    text-transform: uppercase; user-select: none;
    min-width: 52px;
  }
  .nav-item svg { width: 20px; height: 20px; opacity: 0.7; }
  .nav-item:hover { color: #94a3b8; }
  .nav-item:hover svg { opacity: 1; }
  .nav-item.active {
    background: rgba(255,255,255,0.07);
    color: #e5e2e1;
  }
  .nav-item.active svg { opacity: 1; }

  /* ── LOGIN ──────────────────────────────────────────────────────────── */
  .login-page {
    min-height: 100vh; display: flex; align-items: center; justify-content: center;
    background: var(--bg); padding: 20px;
  }
  .login-card {
    background: var(--bg-low); border: 1px solid var(--border2);
    border-radius: 12px; padding: 36px 28px; width: 100%; max-width: 400px;
    animation: slideUp 0.2s ease;
  }
  .login-logo { text-align: center; margin-bottom: 32px; }
  .login-logo-mark {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 24px; font-weight: 900; letter-spacing: -0.04em;
    text-transform: uppercase; color: #94a3b8;
  }
  .login-logo-mark span { color: var(--primary); }
  .login-logo-sub {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 10px; color: var(--muted); margin-top: 4px;
    letter-spacing: 0.2em; text-transform: uppercase;
  }
  .login-title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 14px; font-weight: 700; margin-bottom: 6px; color: var(--text);
    text-transform: uppercase; letter-spacing: 0.05em;
  }
  .login-error {
    background: var(--red-g); border: 1px solid rgba(255,180,171,0.25);
    border-radius: 8px; padding: 10px 14px; font-size: 12px; color: var(--red); margin-bottom: 14px;
  }
  .login-toggle { text-align: center; font-size: 12px; color: var(--muted); margin-top: 16px; }
  .login-toggle span { color: var(--primary); cursor: pointer; }

  /* ── BUTTONS ────────────────────────────────────────────────────────── */
  .btn {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 11px 20px; border-radius: 8px;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 11px; font-weight: 700; letter-spacing: 0.12em;
    text-transform: uppercase; cursor: pointer;
    transition: all 0.15s; border: none; white-space: nowrap;
  }
  .btn-primary {
    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dim) 100%);
    color: var(--on-primary);
  }
  .btn-primary:hover { opacity: 0.9; transform: translateY(-1px); }
  .btn-primary:disabled { opacity: 0.5; transform: none; cursor: not-allowed; }
  .btn-ghost {
    background: transparent; color: var(--text);
    border: 1px solid var(--border2);
  }
  .btn-ghost:hover { background: var(--bg-high); }
  .btn-danger { background: var(--red-g); color: var(--red); border: 1px solid rgba(255,180,171,0.2); }
  .btn-danger:hover { background: rgba(255,180,171,0.2); }
  .btn-sm { padding: 8px 14px; font-size: 10px; }
  .btn-xs { padding: 5px 10px; font-size: 9px; }

  /* ── CARDS ──────────────────────────────────────────────────────────── */
  .card {
    background: var(--bg-low); border-radius: 8px; overflow: hidden;
    position: relative;
  }
  .card-header {
    padding: 16px 18px; display: flex; align-items: center;
    justify-content: space-between; flex-wrap: wrap; gap: 8px;
  }
  .card-title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 12px; font-weight: 700; color: var(--text);
    text-transform: uppercase; letter-spacing: 0.12em;
  }
  .card-body { padding: 18px; }

  /* ── STAT CARDS ─────────────────────────────────────────────────────── */
  .stats-row { display: grid; grid-template-columns: repeat(2,1fr); gap: 12px; margin-bottom: 20px; }
  .stat-card {
    background: var(--bg-low); border-radius: 8px; padding: 16px 18px;
    position: relative; overflow: hidden;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    text-align: center;
  }
  .stat-card:hover { background: var(--bg-high); }
  .stat-card-top { display: none; }
  .stat-value {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 28px; font-weight: 700; line-height: 1;
    margin-top: 4px; margin-bottom: 4px;
    color: var(--primary);
  }
  .stat-value.accent { color: var(--primary); }
  .stat-value.teal   { color: var(--tertiary); }
  .stat-value.green  { color: var(--primary); }
  .stat-value.orange { color: var(--tertiary); }
  .stat-label {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 9px; color: var(--muted); font-weight: 700;
    letter-spacing: 0.18em; text-transform: uppercase;
  }
  .stat-icon { display: none; }

  /* ── EXERCISE INITIAL BADGES ─────────────────────────────────────────── */
  .ex-initial {
    width: 56px; height: 56px; border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 18px; font-weight: 900; flex-shrink: 0;
    letter-spacing: -0.03em;
    background: var(--bg-highest); color: var(--primary);
    border: 1px solid var(--border);
  }
  .ex-initial-accent { background: var(--bg-highest); color: var(--primary); }
  .ex-initial-teal   { background: var(--bg-highest); color: var(--tertiary); }
  .ex-initial-green  { background: var(--bg-highest); color: var(--primary); }
  .ex-initial-orange { background: var(--bg-highest); color: var(--tertiary); }

  /* ── EX STAT ROW ────────────────────────────────────────────────────── */
  .ex-stat-row {
    display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 0;
    border-top: 1px solid var(--border);
  }
  .ex-stat { padding: 10px 0; text-align: center; border-right: 1px solid var(--border); }
  .ex-stat:last-child { border-right: none; }
  .ex-stat-value {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 15px; font-weight: 700; line-height: 1; margin-bottom: 2px;
  }
  .ex-stat-label {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 8px; color: var(--muted); text-transform: uppercase; letter-spacing: 0.1em;
  }

  /* ── AVATAR CIRCLES ─────────────────────────────────────────────────── */
  .avatar-circle {
    width: 34px; height: 34px; border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 11px; font-weight: 700; flex-shrink: 0;
    background: var(--bg-highest); color: var(--primary);
  }
  .avatar-accent { background: var(--bg-highest); color: var(--primary); }
  .avatar-teal   { background: var(--bg-highest); color: var(--tertiary); }
  .avatar-green  { background: var(--bg-highest); color: var(--primary); }
  .avatar-orange { background: var(--bg-highest); color: var(--tertiary); }

  /* ── TABLES ─────────────────────────────────────────────────────────── */
  .data-table { width: 100%; border-collapse: collapse; }
  .data-table th {
    text-align: left; padding: 10px 16px;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 9px; font-weight: 700; letter-spacing: 0.15em;
    text-transform: uppercase; color: var(--muted);
    background: var(--bg-high);
  }
  .data-table td { padding: 12px 16px; font-size: 12px; vertical-align: middle; }
  .data-table tbody tr { cursor: pointer; transition: background 0.1s; }
  .data-table tbody tr:hover td { background: rgba(185,200,222,0.04); }
  .data-table tbody tr:not(:last-child) td { border-bottom: 1px solid var(--border); }

  /* ── BADGES ─────────────────────────────────────────────────────────── */
  .badge {
    display: inline-flex; align-items: center; padding: 2px 8px;
    border-radius: 4px;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 9px; font-weight: 700; letter-spacing: 0.1em;
    text-transform: uppercase;
  }
  .badge-accent { background: rgba(185,200,222,0.1); color: var(--primary); }
  .badge-teal   { background: rgba(225,193,157,0.1); color: var(--tertiary); }
  .badge-slate  { background: rgba(196,198,205,0.1); color: var(--slate); }
  .badge-red    { background: var(--red-g); color: var(--red); }
  .badge-green  { background: rgba(185,200,222,0.1); color: var(--primary); }

  /* ── MODAL ──────────────────────────────────────────────────────────── */
  body:has(.modal-overlay) { overflow: hidden; }
  .modal-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,0.75);
    display: flex; align-items: center; justify-content: center;
    z-index: 200; padding: 16px; animation: fadeIn 0.15s ease;
  }
  @keyframes fadeIn { from{opacity:0} to{opacity:1} }
  .modal {
    background: var(--bg-low); border: 1px solid var(--border2);
    border-radius: 12px; width: 100%; max-height: 90vh; overflow-y: auto;
    animation: slideUp 0.18s ease; box-shadow: 0 24px 64px rgba(0,0,0,0.6);
  }
  @keyframes slideUp { from{transform:translateY(12px);opacity:0} to{transform:none;opacity:1} }
  .modal-sm  { max-width: 420px; }
  .modal-md  { max-width: 540px; }
  .modal-lg  { max-width: 700px; }
  .modal-header {
    padding: 20px 22px 16px; display: flex; align-items: center;
    justify-content: space-between; position: sticky; top: 0;
    background: var(--bg-low); z-index: 1; border-bottom: 1px solid var(--border);
  }
  .modal-title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 14px; font-weight: 700; color: var(--text);
    text-transform: uppercase; letter-spacing: 0.06em;
  }
  .modal-close {
    width: 30px; height: 30px; border-radius: 6px;
    background: var(--bg-high); border: 1px solid var(--border2);
    color: var(--muted); cursor: pointer; font-size: 14px;
    display: flex; align-items: center; justify-content: center;
    transition: all 0.12s; font-family: inherit;
  }
  .modal-close:hover { color: var(--text); background: var(--bg-highest); }
  .modal-body { padding: 20px 22px; display: flex; flex-direction: column; gap: 16px; }
  .modal-footer {
    padding: 16px 22px; border-top: 1px solid var(--border);
    display: flex; gap: 10px; justify-content: flex-end;
  }

  /* ── FORMS ──────────────────────────────────────────────────────────── */
  .form-group { display: flex; flex-direction: column; gap: 6px; }
  .form-label {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 9px; font-weight: 700; color: var(--muted);
    letter-spacing: 0.18em; text-transform: uppercase;
    display: flex; align-items: center; gap: 4px;
  }
  .req-star { color: var(--req); font-size: 11px; line-height: 1; }
  .form-row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  .input, .select, .textarea {
    background: var(--bg-lowest); border: none; border-radius: 8px;
    padding: 12px 14px; color: var(--text);
    font-family: 'Inter', sans-serif; font-size: 13px;
    transition: box-shadow 0.15s; outline: none; width: 100%;
  }
  .input:focus, .select:focus, .textarea:focus {
    box-shadow: 0 0 0 2px rgba(185,200,222,0.35);
  }
  .input.error, .select.error { box-shadow: 0 0 0 2px rgba(255,180,171,0.4); }
  .select { appearance: none; cursor: pointer; }
  .textarea { resize: vertical; min-height: 80px; line-height: 1.6; }
  .input::placeholder, .textarea::placeholder { color: var(--muted); }
  .field-error { font-size: 11px; color: var(--req); margin-top: 2px; }

  /* ── SETS TABLE ─────────────────────────────────────────────────────── */
  .sets-table { width: 100%; border-collapse: collapse; }
  .sets-table th {
    text-align: left; padding: 6px 8px;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 9px; font-weight: 700; letter-spacing: 0.12em;
    text-transform: uppercase; color: var(--muted);
    border-bottom: 1px solid var(--border);
  }
  .sets-table td { padding: 5px 4px; vertical-align: middle; }
  .set-num { font-size: 10px; color: var(--muted); text-align: center; padding: 0 6px; }
  .del-btn {
    width: 26px; height: 26px; border-radius: 6px;
    background: var(--red-g); border: 1px solid rgba(255,180,171,0.2);
    color: var(--red); cursor: pointer; font-size: 14px;
    display: flex; align-items: center; justify-content: center;
    transition: all 0.12s; font-family: inherit;
  }
  .del-btn:hover { background: rgba(255,180,171,0.22); }
  .add-row-btn {
    width: 100%; padding: 8px; margin-top: 6px; border-radius: 8px;
    border: 1px dashed var(--border2); background: transparent;
    color: var(--muted); font-family: 'Inter', sans-serif; font-size: 12px;
    cursor: pointer; transition: all 0.15s;
  }
  .add-row-btn:hover { border-color: var(--primary); color: var(--primary); background: var(--accent-g); }

  /* ── SECTION BLOCK ──────────────────────────────────────────────────── */
  .section-block {
    background: var(--bg-high); border-radius: 8px; padding: 14px;
  }

  /* ── LAYOUTS ────────────────────────────────────────────────────────── */
  .grid-3-1 { display: grid; grid-template-columns: 2fr 1fr; gap: 16px; margin-bottom: 16px; }
  .grid-2   { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px; }

  /* ── WORKOUT CARD ───────────────────────────────────────────────────── */
  .category-grid { display: flex; flex-direction: column; gap: 28px; }
  .category-card {
    background: transparent; overflow: visible;
    position: relative; cursor: pointer;
  }
  .category-card-top { display: none; }
  .category-card-body { padding: 0 0 0 12px; position: relative; }
  .category-card-body::before {
    content: '';
    position: absolute; left: 0; top: 0; bottom: 0;
    width: 2px; background: rgba(185,200,222,0.2);
    border-radius: 2px;
  }
  .category-name {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 22px; font-weight: 700;
    letter-spacing: -0.02em; text-transform: uppercase;
  }
  .category-meta {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 9px; color: var(--muted);
    letter-spacing: 0.18em; text-transform: uppercase;
    font-weight: 700; margin-top: 2px;
  }
  .category-card-footer {
    padding: 0 0 0 12px;
    display: flex; justify-content: space-between; align-items: center;
    margin-top: 10px;
  }

  /* ── EXERCISE CARD ──────────────────────────────────────────────────── */
  .exercise-grid { display: flex; flex-direction: column; gap: 16px; }
  .ex-card {
    background: var(--bg-low); border-radius: 8px; overflow: hidden;
    border-left: 3px solid var(--primary);
    transition: background 0.15s;
  }
  .ex-card:hover { background: var(--bg-high); }
  .ex-card-header { padding: 16px 18px 12px; border-bottom: 1px solid var(--border); }
  .ex-name {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 16px; font-weight: 700;
    text-transform: uppercase; letter-spacing: -0.01em;
  }
  .ex-muscle {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 9px; color: var(--muted); margin-top: 2px;
    text-transform: uppercase; letter-spacing: 0.18em; font-weight: 700;
  }
  .ex-card-body { padding: 12px 18px; }
  .ex-card-footer {
    padding: 10px 18px; border-top: 1px solid var(--border);
    display: flex; justify-content: space-between; align-items: center;
  }

  /* ── MUSCLE PICKER ──────────────────────────────────────────────────── */
  .muscle-picker { display: flex; flex-wrap: wrap; gap: 6px; }
  .muscle-chip {
    padding: 7px 14px; border-radius: 8px; cursor: pointer;
    border: 1px solid var(--border2); background: var(--bg-high);
    color: var(--muted);
    font-family: 'Space Grotesk', sans-serif; font-size: 10px; font-weight: 700;
    letter-spacing: 0.1em; text-transform: uppercase;
    transition: all 0.15s;
  }
  .muscle-chip:hover { color: var(--text); border-color: var(--border2); background: var(--bg-highest); }
  .muscle-chip.active { background: rgba(185,200,222,0.12); color: var(--primary); border-color: rgba(185,200,222,0.3); }

  /* ── DATE RANGE ─────────────────────────────────────────────────────── */
  .date-range { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
  .date-range-sep { color: var(--muted); font-size: 11px; }
  .range-btn {
    padding: 7px 14px; border-radius: 8px; cursor: pointer;
    border: none; background: var(--bg-lowest);
    color: var(--muted);
    font-family: 'Space Grotesk', sans-serif; font-size: 10px; font-weight: 700;
    letter-spacing: 0.12em; text-transform: uppercase;
    transition: all 0.15s; white-space: nowrap;
  }
  .range-btn:hover { color: var(--text); }
  .range-btn.active { background: var(--bg-highest); color: var(--primary); }

  /* ── EMPTY STATE ────────────────────────────────────────────────────── */
  .empty-state { text-align: center; padding: 48px 24px; color: var(--muted); }
  .empty-title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 13px; font-weight: 700; color: var(--text); margin-bottom: 6px;
    text-transform: uppercase; letter-spacing: 0.08em;
  }
  .empty-desc { font-size: 12px; color: var(--muted); }

  /* ── TOOLTIP ────────────────────────────────────────────────────────── */
  .ctooltip {
    background: var(--bg-highest); border: 1px solid var(--border2);
    border-radius: 8px; padding: 10px 14px; font-family: 'Inter', sans-serif;
    font-size: 12px; box-shadow: 0 8px 24px rgba(0,0,0,0.4);
  }
  .ctooltip-label { color: var(--muted); font-size: 10px; margin-bottom: 2px; }
  .ctooltip-val   { font-weight: 700; }

  /* ── FILTERS ────────────────────────────────────────────────────────── */
  .filters { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
  .filter-btn {
    padding: 7px 14px; border-radius: 8px; cursor: pointer;
    border: none; background: var(--bg-low);
    color: var(--muted);
    font-family: 'Space Grotesk', sans-serif; font-size: 10px; font-weight: 700;
    letter-spacing: 0.12em; text-transform: uppercase;
    transition: all 0.15s;
  }
  .filter-btn:hover { color: var(--text); background: var(--bg-high); }
  .filter-btn.active { background: rgba(185,200,222,0.1); color: var(--primary); }
  .search-input {
    background: var(--bg-lowest); border: none; border-radius: 8px;
    padding: 12px 14px; color: var(--text);
    font-family: 'Space Grotesk', sans-serif; font-size: 11px;
    letter-spacing: 0.08em; text-transform: uppercase;
    outline: none; transition: box-shadow 0.15s; width: 100%;
  }
  .search-input:focus { box-shadow: 0 0 0 2px rgba(185,200,222,0.3); }
  .search-input::placeholder { color: var(--muted); }

  /* ── SECTION HEADERS ────────────────────────────────────────────────── */
  .section-title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 16px; font-weight: 700; letter-spacing: -0.01em;
    text-transform: uppercase; color: var(--text);
    margin-bottom: 12px;
  }
  .section-label {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 10px; font-weight: 700; letter-spacing: 0.2em;
    text-transform: uppercase; color: var(--primary);
    margin-bottom: 4px; display: block;
  }

  /* ── PAGE HERO ──────────────────────────────────────────────────────── */
  .page-hero {
    padding: 4px 0 20px; position: relative; overflow: hidden;
  }
  .page-hero-super {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 10px; font-weight: 700; letter-spacing: 0.2em;
    text-transform: uppercase; color: var(--primary); margin-bottom: 4px;
    display: block;
  }
  .page-hero-title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 42px; font-weight: 900; letter-spacing: -0.04em;
    line-height: 0.95; text-transform: uppercase; color: var(--text);
  }

  /* ── LEADERBOARD ────────────────────────────────────────────────────── */
  .leaderboard-row {
    background: var(--bg-low); border-radius: 8px; padding: 14px 16px;
    display: flex; align-items: center; justify-content: space-between;
  }
  .leaderboard-row:hover { background: var(--bg-high); }

  /* ── PROGRESS RINGS (decorative) ────────────────────────────────────── */
  .ring-container { position: relative; display: inline-flex; }
  .ring-label {
    position: absolute; inset: 0;
    display: flex; align-items: center; justify-content: center;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 18px; font-weight: 700;
  }

  /* ── METALLIC SHEEN BUTTON ──────────────────────────────────────────── */
  .btn-metallic {
    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dim) 100%);
    color: var(--on-primary); border: none; border-radius: 8px;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 11px; font-weight: 700; letter-spacing: 0.14em;
    text-transform: uppercase; cursor: pointer; padding: 14px;
    width: 100%; transition: opacity 0.15s; display: block; text-align: center;
  }
  .btn-metallic:hover { opacity: 0.9; }

  /* ── EXERCISE ROW (in workout card) ─────────────────────────────────── */
  .wk-ex-row {
    display: flex; justify-content: space-between; align-items: center;
    padding: 10px 0; border-bottom: 1px solid var(--border);
  }
  .wk-ex-row:last-child { border-bottom: none; }
  .wk-ex-name {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 12px; font-weight: 700; text-transform: uppercase;
    color: var(--text);
  }
  .wk-ex-meta {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 9px; color: var(--muted); letter-spacing: 0.1em;
    text-transform: uppercase; margin-top: 1px;
  }
  .wk-ex-weight {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 14px; font-weight: 700; color: var(--primary); text-align: right;
  }
  .wk-ex-weight-label {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 8px; color: var(--muted); text-transform: uppercase;
    letter-spacing: 0.1em; text-align: right;
  }

  /* ── PROFILE ────────────────────────────────────────────────────────── */
  .profile-avatar {
    width: 96px; height: 96px; border-radius: 12px;
    display: flex; align-items: center; justify-content: center;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 32px; font-weight: 900; color: var(--primary);
    background: var(--bg-high); border: 2px solid rgba(185,200,222,0.2);
    position: relative;
  }
  .profile-rank-badge {
    position: absolute; bottom: -10px; left: 50%; transform: translateX(-50%);
    background: var(--primary); color: var(--on-primary);
    padding: 3px 10px; border-radius: 4px;
    font-family: 'Space Grotesk', sans-serif;
    font-size: 9px; font-weight: 700; letter-spacing: 0.14em;
    text-transform: uppercase; white-space: nowrap;
  }

  /* ── SECTION DIVIDER ─────────────────────────────────────────────────── */
  .section-divider {
    display: flex; align-items: center; gap: 12px; margin: 24px 0 16px;
  }
  .section-divider-line { flex: 1; height: 1px; background: var(--border); }
  .section-divider-label {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 10px; font-weight: 700; letter-spacing: 0.25em;
    text-transform: uppercase; color: var(--muted);
  }

  /* ── TOGGLE ─────────────────────────────────────────────────────────── */
  .toggle-track {
    width: 44px; height: 24px; border-radius: 12px;
    background: var(--bg-highest); position: relative;
    cursor: pointer; transition: background 0.2s; flex-shrink: 0;
  }
  .toggle-track.on { background: var(--primary); }
  .toggle-thumb {
    position: absolute; top: 3px; left: 3px;
    width: 18px; height: 18px; border-radius: 9px;
    background: var(--on-primary); transition: left 0.2s;
    box-shadow: 0 1px 3px rgba(0,0,0,0.3);
  }
  .toggle-track.on .toggle-thumb { left: calc(100% - 21px); }

  /* ── ADMIN ──────────────────────────────────────────────────────────── */
  .infra-health {
    background: var(--bg-low); border-radius: 8px; padding: 20px;
    position: relative; overflow: hidden; margin-bottom: 24px;
  }
  .infra-uptime {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 40px; font-weight: 700; color: var(--primary);
    font-style: italic; letter-spacing: -0.03em;
  }

  /* ── FILTER CHIPS (horizontal scroll) ──────────────────────────────── */
  .filter-chips {
    display: flex; gap: 8px; overflow-x: auto;
    padding-bottom: 4px; margin-bottom: 16px;
    scrollbar-width: none;
  }
  .filter-chips::-webkit-scrollbar { display: none; }
  .chip {
    padding: 8px 18px; border-radius: 8px; cursor: pointer; border: none;
    background: var(--bg-low); color: var(--muted);
    font-family: 'Space Grotesk', sans-serif; font-size: 10px; font-weight: 700;
    letter-spacing: 0.14em; text-transform: uppercase;
    transition: all 0.15s; white-space: nowrap; flex-shrink: 0;
  }
  .chip:hover { background: var(--bg-high); color: var(--text); }
  .chip.active {
    background: var(--primary-dim); color: var(--on-primary);
  }

  /* ── LOADING SCREEN ─────────────────────────────────────────────────── */
  .loading-screen {
    min-height: 100vh; display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    background: var(--bg);
  }
  .loading-dot {
    width: 6px; height: 6px; border-radius: 50%;
    background: var(--primary); opacity: 0.4;
    animation: loadPulse 1.2s infinite;
  }
  .loading-dot:nth-child(2) { animation-delay: 0.2s; }
  .loading-dot:nth-child(3) { animation-delay: 0.4s; }
  @keyframes loadPulse { 0%,100%{opacity:0.2;transform:scale(1)} 50%{opacity:1;transform:scale(1.3)} }

  /* ── ANIMATIONS ─────────────────────────────────────────────────────── */
  @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }
  .animate-pulse { animation: pulse 2s infinite; }
`;

export default css;
